import { log } from "./log.js";
/**
 * Editor lifecycle state machine.
 *
 * Replaces the implicit lifecycle previously encoded as ~30 null-checks
 * scattered across `state.interop`, `state.pdfDoc`, and `state.ir`. The
 * machine pins the legal transitions so reset-vs-Tier2-load races
 * (cancel a Tier-2 background load that arrived after the user reset
 * the editor) cannot land partial state.
 *
 * Pattern mirrors `web/pipeline.py:JobStateMachine` (Enum-style states +
 * transition table + on_enter hooks). See CLAUDE.md
 * "Explicit state machines for async job lifecycles" — same shape.
 *
 * States:
 *   EMPTY_COLD    boot, before WASM has been built
 *   EMPTY_WARM    after a reset; WASM (state.interop) is preserved so the
 *                 next file load is a "warm" ~2s instead of ~8-10s cold
 *   LOADING       loadFile() in flight: WASM bootstrap, pdf-parse, page-1 paint
 *   LOADED_TIER1  page-1 painted; user can scroll/zoom but cards/IR aren't
 *                 wired yet (background fill-in pending)
 *   LOADED_FULL   IR + cards + screen-reader panel mounted; full editor ready
 *
 * Tier-2 race resolution:
 *   When `_scheduleTier2Loading` resolves AFTER the user has reset the
 *   editor (LOADED_TIER1 -> EMPTY_WARM transition), the late-arriving
 *   tier-2 result must be REJECTED. The contract:
 *
 *     - `loadFile()` calls transition(LOADING) before any async work,
 *       capturing the current generation/version.
 *     - The tier-2 callback checks `is(LOADED_TIER1)` before mutating
 *       state. If the machine is in EMPTY_WARM (because reset fired
 *       between paint and idle-callback), the callback returns
 *       early and does NOT install ir/cards/sr-panel.
 *     - This is single-threaded JS event-loop semantics: between the
 *       check and the transition there is no preemption point.
 */
export const EditorLifecycleState = Object.freeze({
    EMPTY_COLD: "empty_cold",
    EMPTY_WARM: "empty_warm",
    LOADING: "loading",
    LOADED_TIER1: "loaded_tier1",
    LOADED_FULL: "loaded_full",
});
// WHY: Readonly<Record<string, ...>> annotation is intentional — this table is
// keyed by this._state: string at runtime (not a closed-key enum). The T|undefined
// return under noUncheckedIndexedAccess is correct; callsites guard with !valid.
const TRANSITIONS = Object.freeze({
    [EditorLifecycleState.EMPTY_COLD]: new Set([
        EditorLifecycleState.LOADING,
    ]),
    [EditorLifecycleState.EMPTY_WARM]: new Set([
        EditorLifecycleState.LOADING,
    ]),
    [EditorLifecycleState.LOADING]: new Set([
        EditorLifecycleState.LOADED_TIER1,
        EditorLifecycleState.EMPTY_COLD,
        EditorLifecycleState.EMPTY_WARM,
    ]),
    [EditorLifecycleState.LOADED_TIER1]: new Set([
        EditorLifecycleState.LOADED_FULL,
        EditorLifecycleState.EMPTY_WARM,
    ]),
    // A loaded document may ONLY leave via EMPTY_WARM (i.e. through
    // resetEditorState). The former LOADED_FULL → LOADING edge was removed in
    // editor-open-load-state-coherence-260813 Phase 2: it admitted a
    // document-switch that skipped the atomic reset, silently reusing the prior
    // document's jobId/interop and re-rendering its IR (BUG #5). Opening another
    // document now routes through `loadDocument` (editor.ts), which resets to
    // EMPTY_WARM first — making "switch without reset" an illegal, fail-loud
    // transition rather than a stale render (→ A.8 make-the-error-impossible).
    [EditorLifecycleState.LOADED_FULL]: new Set([
        EditorLifecycleState.EMPTY_WARM,
    ]),
});
const TERMINAL_STATES = new Set([
    EditorLifecycleState.EMPTY_COLD,
    EditorLifecycleState.EMPTY_WARM,
    EditorLifecycleState.LOADED_FULL,
]);
export class EditorStateMachine {
    _state;
    _onEnter;
    _onInvalid;
    _inTransition;
    _generation;
    constructor(opts = {}) {
        this._state = opts.initial ?? EditorLifecycleState.EMPTY_COLD;
        this._onEnter = opts.onEnter ?? {};
        this._onInvalid = opts.onInvalid ?? null;
        this._inTransition = false;
        this._generation = 0;
    }
    get state() { return this._state; }
    is(target) {
        return this._state === target;
    }
    isAny(candidates) {
        for (const c of candidates) {
            if (this._state === c)
                return true;
        }
        return false;
    }
    get generation() { return this._generation; }
    transition(next) {
        if (this._inTransition) {
            throw new Error(`[editor-state] re-entrant transition: cannot transition to ${next} ` +
                `while another transition is in flight (current=${this._state})`);
        }
        const valid = TRANSITIONS[this._state];
        if (!valid?.has(next)) {
            const err = new Error(`[editor-state] illegal transition ${this._state} -> ${next}. ` +
                `Valid targets from ${this._state}: ${valid ? Array.from(valid).join(", ") : "<none>"}`);
            if (this._onInvalid) {
                this._onInvalid({ from: this._state, to: next, valid });
            }
            throw err;
        }
        const prev = this._state;
        this._inTransition = true;
        try {
            this._state = next;
            if (next === EditorLifecycleState.EMPTY_COLD ||
                next === EditorLifecycleState.EMPTY_WARM) {
                this._generation += 1;
            }
            const hook = this._onEnter[next];
            if (hook)
                hook(prev, next);
        }
        finally {
            this._inTransition = false;
        }
    }
    tryTransition(next) {
        const valid = TRANSITIONS[this._state];
        if (!valid?.has(next))
            return false;
        this.transition(next);
        return true;
    }
    ensureTerminal() {
        if (TERMINAL_STATES.has(this._state))
            return;
        log.error(`[editor-state] ensureTerminal: non-terminal state ${this._state} at teardown. ` +
            `This is usually a bug — a load was in flight when the page unloaded.`);
    }
}
export function lifecycleQueries(sm) {
    return {
        hasPdf: () => sm.isAny([
            EditorLifecycleState.LOADED_TIER1,
            EditorLifecycleState.LOADED_FULL,
        ]),
        hasIr: () => sm.is(EditorLifecycleState.LOADED_FULL),
        hasInterop: () => !sm.is(EditorLifecycleState.EMPTY_COLD),
        isReady: () => sm.is(EditorLifecycleState.LOADED_FULL),
    };
}
